import {Drawer} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {IconCalendarDue, IconDeviceDesktop} from '@tabler/icons-react';
import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import type {
  InspectionAssigneeCandidateResponse,
  InspectionSimpleResponse,
} from 'src/api/ProactApiClient';
import {
  DeficiencyStatusEnum,
  InspectionStateEnum,
} from 'src/api/ProactApiClient';
import {cadenceLabel} from 'src/app/inspections/schedules/inspectionScheduleHelpers';
import {Button} from 'src/design/atoms/Button';
import {ContentTitle} from 'src/design/atoms/Typography';
import {NavAnchor} from 'src/design/atoms/NavAnchor';
import {Pill} from 'src/design/atoms/Pill';
import {Textbox} from 'src/design/atoms/Textbox';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {StatTile} from 'src/design/molecules/StatTile';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {
  useAssignInspectionMutation,
  useDeleteInspectionMutation,
  useGetInspectionAssigneeCandidatesQuery,
  useGetInspectionQuery,
  useTransitionInspectionMutation,
} from 'src/redux/queries/inspection';
import {useGetInspectionDeficiencyListQuery} from 'src/redux/queries/deficiency';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {formatUtcCalendarDate} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {AssignCandidateRow} from './AssignCandidateRow';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface InspectionDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  inspection: InspectionSimpleResponse | undefined;
}

const DrawerHeader: React.FC<{
  inspection: InspectionSimpleResponse;
  showViewLink?: boolean;
}> = ({inspection, showViewLink}) => {
  const perms = useInspectionPermissions();
  const dueAt = inspection.dueAt ?? undefined;
  // A soft-deleted schedule keeps its id but loses the cadence; the pill stays
  // for provenance while the link is dropped.
  const scheduleCadence = inspection.inspectionScheduleCadence;
  const scheduleId = inspection.inspectionScheduleId;
  const schedulePillText = scheduleCadence
    ? `Recurring · ${cadenceLabel({
        cadence: scheduleCadence,
        intervalDays: inspection.inspectionScheduleIntervalDays,
      })}`
    : 'Recurring';

  return (
    <>
      <div>
        <ContentTitle text={inspection.siteName} />
        <Text size="sm" c="dimmed">
          {inspection.clientName ?? '—'}
          {inspection.siteNumber ? ` · #${inspection.siteNumber}` : ''}
        </Text>
      </div>

      {scheduleId !== undefined && (
        <Group gap="sm">
          <Pill text={schedulePillText} color="gray" />
          {scheduleCadence !== undefined && perms.manageSchedules && (
            <NavAnchor
              text="View schedule"
              component={Link}
              to={`${PathUtility.Inspections('schedules')}?schedule=${scheduleId}`}
            />
          )}
        </Group>
      )}

      <SimpleGrid cols={2}>
        <StatTile
          title="Devices"
          value={inspection.scopedDeviceCount}
          icon={<IconDeviceDesktop size={20} />}
        />
        <StatTile
          title="Due"
          value={formatUtcCalendarDate(dueAt)}
          icon={<IconCalendarDue size={20} />}
        />
      </SimpleGrid>

      {showViewLink && <ViewFullInspectionLink id={inspection.id} />}

      <Divider />
    </>
  );
};

// Same `assign` mutation handles both assign and reassign — backend allows
// it until Completed.
const CandidateList: React.FC<{
  inspection: InspectionSimpleResponse;
  actionLabel: string;
  successMessage: string;
  onDone: () => void;
  opened: boolean;
}> = ({inspection, actionLabel, successMessage, onDone, opened}) => {
  const {setAlert} = useAlert();
  const [search, setSearch] = useState('');
  const [assigningUserId, setAssigningUserId] = useState<number>();
  const [assignInspection] = useAssignInspectionMutation();

  const {data: candidates} = useGetInspectionAssigneeCandidatesQuery(
    undefined,
    {
      skip: !opened,
    }
  );

  const filteredCandidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = candidates ?? [];
    return term ? list.filter(c => c.name.toLowerCase().includes(term)) : list;
  }, [candidates, search]);

  const handleAssign = async (
    candidate: InspectionAssigneeCandidateResponse
  ) => {
    setAssigningUserId(candidate.userId);
    try {
      await assignInspection({
        id: inspection.id,
        request: {assigneeId: candidate.userId},
      }).unwrap();
      setAlert(successMessage, 'success');
      setSearch('');
      onDone();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setAssigningUserId(undefined);
    }
  };

  return (
    <>
      <Textbox
        ariaLabel="Search candidates"
        placeholder="Search candidates"
        value={search}
        onChangeCallback={e => setSearch(e.target.value)}
      />

      {filteredCandidates.length === 0 ? (
        <NoItemsPanel noItemsText="No candidates found." />
      ) : (
        <Stack gap={0}>
          {filteredCandidates.map((candidate, index) => (
            <React.Fragment key={candidate.userId}>
              {index > 0 && <Divider />}
              <AssignCandidateRow
                candidate={candidate}
                onAssign={handleAssign}
                loading={assigningUserId === candidate.userId}
                actionLabel={actionLabel}
              />
            </React.Fragment>
          ))}
        </Stack>
      )}
    </>
  );
};

const ViewFullInspectionLink: React.FC<{id: number}> = ({id}) => (
  <NavAnchor
    text="View full inspection"
    component={Link}
    to={PathUtility.InspectionsView(id)}
  />
);

const CompletedContent: React.FC<{
  inspection: InspectionSimpleResponse;
  opened: boolean;
}> = ({inspection, opened}) => {
  const {data: detail} = useGetInspectionQuery(inspection.id, {skip: !opened});
  const {data: deficiencies} = useGetInspectionDeficiencyListQuery(
    {
      id: inspection.id,
      inspectionId: inspection.id,
      statuses: [DeficiencyStatusEnum.Open],
      start: 0,
      length: 1,
    },
    {skip: !opened}
  );
  const openCount = deficiencies?.filteredRecords ?? 0;

  return (
    <Stack>
      <DrawerHeader inspection={inspection} showViewLink />

      <Alert color="green" variant="light">
        Completed{' '}
        {detail?.completedAt
          ? parseUtcDate(detail.completedAt).toLocaleDateString()
          : '—'}{' '}
        by {inspection.assigneeName ?? 'Unassigned'}.
      </Alert>

      <SimpleGrid cols={3}>
        <InlineStat label="Pass" value={detail?.passCount ?? 0} />
        <InlineStat label="Fail" value={detail?.failCount ?? 0} />
        <InlineStat label="No Access" value={detail?.noAccessCount ?? 0} />
      </SimpleGrid>

      <div>
        <Text size="sm" fw={600} mb="xs">
          Deficiencies
        </Text>
        {openCount > 0 ? (
          <Alert color="red" variant="light">
            <Stack gap={4}>
              <Text size="sm">
                {openCount} open deficienc{openCount === 1 ? 'y' : 'ies'} —
                generated from failed devices, tracked for follow-up
              </Text>
              <NavAnchor
                text="View deficiencies"
                component={Link}
                to={`${PathUtility.InspectionsView(
                    inspection.id
                  )}?tab=deficiencies`}
              />
            </Stack>
          </Alert>
        ) : (
          <Text size="sm" c="dimmed">
            No open deficiencies.
          </Text>
        )}
      </div>
    </Stack>
  );
};

// Draft/Scheduled admin actions — schedule/unschedule, edit, delete. Hidden
// entirely once In Progress/Completed (those states have their own flows).
const AdminActions: React.FC<{
  inspection: InspectionSimpleResponse;
  onClose: () => void;
}> = ({inspection, onClose}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const perms = useInspectionPermissions();
  const [transitionInspection, {isLoading: isTransitioning}] =
    useTransitionInspectionMutation();
  const [deleteInspection] = useDeleteInspectionMutation();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isDraft = inspection.state === InspectionStateEnum.Draft;
  const isScheduled = inspection.state === InspectionStateEnum.Scheduled;

  if (!isDraft && !isScheduled) return null;

  const handleTransition = async (transition: 'schedule' | 'unschedule') => {
    try {
      await transitionInspection({
        id: inspection.id,
        request: {transition},
      }).unwrap();
      setAlert(
        transition === 'schedule'
          ? 'Inspection scheduled.'
          : 'Inspection moved back to draft.',
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDelete = async () => {
    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteInspection(inspection.id).unwrap();
      setAlert('Inspection was successfully deleted.', 'success');
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
  };

  return (
    <>
      <Group>
        {isDraft && perms.update && (
          <Button
            onClick={() => handleTransition('schedule')}
            loading={isTransitioning}>
            Schedule
          </Button>
        )}
        {isScheduled && perms.update && (
          <Button
            variant="subtle"
            onClick={() => handleTransition('unschedule')}
            loading={isTransitioning}>
            Unschedule
          </Button>
        )}
        {perms.update && (
          <Button
            variant="subtle"
            onClick={() =>
              navigate(
                PathUtility.InspectionsEdit(inspection.id)
              )
            }>
            Edit
          </Button>
        )}
        {perms.delete && (
          <Button
            variant="subtle"
            color="red"
            onClick={() => confirmDeleteHandlers.open()}>
            Delete
          </Button>
        )}
      </Group>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title="Are you sure you want to delete this inspection?"
        body="This action cannot be undone."
      />
    </>
  );
};

const UnassignedContent: React.FC<{
  inspection: InspectionSimpleResponse;
  onClose: () => void;
  opened: boolean;
}> = ({inspection, onClose, opened}) => (
  <Stack>
    <DrawerHeader inspection={inspection} />
    <AdminActions inspection={inspection} onClose={onClose} />
    <CandidateList
      inspection={inspection}
      actionLabel="Assign"
      successMessage="Inspection assigned."
      onDone={onClose}
      opened={opened}
    />
  </Stack>
);

const AssignedContent: React.FC<{
  inspection: InspectionSimpleResponse;
  onClose: () => void;
  opened: boolean;
}> = ({inspection, onClose, opened}) => (
  <Stack>
    <DrawerHeader inspection={inspection} showViewLink />
    <AdminActions inspection={inspection} onClose={onClose} />
    <Text size="sm">
      Currently assigned to <b>{inspection.assigneeName}</b>. Pick a different
      inspector to reassign.
    </Text>
    <CandidateList
      inspection={inspection}
      actionLabel="Reassign"
      successMessage="Inspection reassigned."
      onDone={onClose}
      opened={opened}
    />
  </Stack>
);

/** Read/act drawer for a single inspection row — completed summary, assign, or reassign. */
export const InspectionDetailDrawer: React.FC<InspectionDetailDrawerProps> = ({
  opened,
  onClose,
  inspection,
}) => {
  const isCompleted = inspection?.state === InspectionStateEnum.Completed;
  const isUnassigned = !inspection?.assigneeId;

  const title = !inspection
    ? ''
    : isCompleted
    ? 'Inspection Complete'
    : isUnassigned
    ? 'Assign Inspection'
    : 'Reassign Inspection';

  return (
    <Drawer opened={opened} onClose={onClose} position="right" title={title}>
      {inspection &&
        (isCompleted ? (
          <CompletedContent inspection={inspection} opened={opened} />
        ) : isUnassigned ? (
          <UnassignedContent
            inspection={inspection}
            onClose={onClose}
            opened={opened}
          />
        ) : (
          <AssignedContent
            inspection={inspection}
            onClose={onClose}
            opened={opened}
          />
        ))}
    </Drawer>
  );
};
